/**
 * Region plausibility check for geocoded places.
 *
 * A place string usually names its state/province/country ("stratford, fairfield,
 * connecticut"). If a geocoder result lands outside that region's padded bounding
 * box, the result is wrong (e.g. "new london ct" → Nevada). Shared by
 * GeocoderService, the seed builder, and the seed audit test. Pure functions, no I/O.
 *
 * "Inside the named region" is a plausibility check, not proof the point is the
 * right town.
 */

export interface Box {
  south: number;
  west: number;
  north: number;
  east: number;
}

/** Degrees added on every side so border towns and coastal points are not rejected. */
const PAD = 0.5;

function padded(south: number, west: number, north: number, east: number): Box {
  return { south: south - PAD, west: west - PAD, north: north + PAD, east: east + PAD };
}

function union(...boxes: Box[]): Box {
  return {
    south: Math.min(...boxes.map((b) => b.south)),
    west: Math.min(...boxes.map((b) => b.west)),
    north: Math.max(...boxes.map((b) => b.north)),
    east: Math.max(...boxes.map((b) => b.east)),
  };
}

const NEW_ENGLAND_STATES = {
  maine: padded(43.06, -71.08, 47.46, -66.95),
  'new hampshire': padded(42.7, -72.56, 45.31, -70.61),
  vermont: padded(42.73, -73.44, 45.02, -71.46),
  massachusetts: padded(41.24, -73.51, 42.89, -69.93),
  'rhode island': padded(41.15, -71.91, 42.02, -71.12),
  connecticut: padded(40.95, -73.73, 42.05, -71.79),
};
const NEW_YORK = padded(40.5, -79.76, 45.02, -71.86);
const NEW_JERSEY = padded(38.93, -75.56, 41.36, -73.89);
const NETHERLANDS = padded(50.75, 3.36, 53.56, 7.23);
const BELGIUM = padded(49.5, 2.39, 51.55, 6.41);

/**
 * Country-level regions that umbrella the state/province boxes. A place naming both
 * ("gilroy, california, united states") is checked against the more specific one.
 */
const COUNTRY_LEVEL = new Set(['united states', 'canada']);

/**
 * Region key for places named by a colonial jurisdiction ("plymouth colony",
 * "province of new york"). Colonial borders differ from modern states — Maine was
 * part of Massachusetts Bay, Martha's Vineyard was governed by New York — so these
 * places are checked against the whole colonial Northeast. That still rejects the
 * v0.1.0 errors (Nevada, Georgia) without rejecting the right modern town.
 */
export const COLONIAL_NORTHEAST = 'colonial northeast';

/**
 * Padded lat/lng boxes keyed by the lowercase region token as it appears in
 * normalized place strings. Bounds are approximate administrative extents. States,
 * provinces and countries added in P-15d come from Nominatim boundingbox values
 * fetched 2026-09-14; Alaska and Norway are clamped (their boxes cross the
 * antimeridian or include remote islands).
 */
export const REGIONS: Record<string, Box> = {
  ...NEW_ENGLAND_STATES,
  'new england': union(...Object.values(NEW_ENGLAND_STATES)),
  [COLONIAL_NORTHEAST]: union(...Object.values(NEW_ENGLAND_STATES), NEW_YORK, NEW_JERSEY),
  'new york': NEW_YORK,
  'new jersey': NEW_JERSEY,
  pennsylvania: padded(39.72, -80.52, 42.27, -74.69),
  delaware: padded(38.45, -75.79, 39.84, -75.05),
  maryland: padded(37.89, -79.49, 39.72, -75.05),
  virginia: padded(36.54, -83.68, 39.47, -75.24),
  'west virginia': padded(37.2, -82.64, 40.64, -77.72),
  'north carolina': padded(33.84, -84.32, 36.59, -75.46),
  'south carolina': padded(32.03, -83.35, 35.22, -78.54),
  georgia: padded(30.36, -85.61, 35.0, -80.84),
  ohio: padded(38.4, -84.82, 41.98, -80.52),
  indiana: padded(37.77, -88.1, 41.76, -84.78),
  illinois: padded(36.97, -91.51, 42.51, -87.02),
  kentucky: padded(36.5, -89.57, 39.15, -81.96),
  tennessee: padded(34.98, -90.31, 36.68, -81.65),
  michigan: padded(41.7, -90.42, 48.31, -82.12),
  wisconsin: padded(42.49, -92.89, 47.31, -86.25),
  iowa: padded(40.38, -96.64, 43.5, -90.14),
  missouri: padded(35.99, -95.77, 40.61, -89.1),
  alabama: padded(30.14, -88.47, 35.01, -84.89),
  alaska: padded(51.0, -180, 71.59, -129.97),
  arizona: padded(31.33, -114.82, 37.0, -109.04),
  arkansas: padded(33.0, -94.62, 36.5, -89.64),
  california: padded(32.53, -124.48, 42.01, -114.13),
  colorado: padded(36.99, -109.06, 41.0, -102.04),
  florida: padded(24.4, -87.63, 31.0, -79.97),
  hawaii: padded(18.87, -178.44, 28.52, -154.76),
  idaho: padded(41.99, -117.24, 49.0, -111.04),
  kansas: padded(36.99, -102.05, 40.0, -94.59),
  louisiana: padded(28.85, -94.04, 33.02, -88.76),
  minnesota: padded(43.5, -97.24, 49.38, -89.48),
  mississippi: padded(30.14, -91.66, 35.0, -88.1),
  montana: padded(44.36, -116.05, 49.0, -104.04),
  nebraska: padded(40.0, -104.05, 43.0, -95.31),
  nevada: padded(35.0, -120.01, 42.0, -114.04),
  'new mexico': padded(31.33, -109.05, 37.0, -103.0),
  'north dakota': padded(45.93, -104.05, 49.0, -96.55),
  oklahoma: padded(33.62, -103.0, 37.0, -94.43),
  oregon: padded(41.99, -124.7, 46.29, -116.46),
  'south dakota': padded(42.48, -104.06, 45.95, -96.44),
  texas: padded(25.84, -106.65, 36.5, -93.51),
  utah: padded(37.0, -114.05, 42.0, -109.04),
  washington: padded(45.54, -124.84, 49.0, -116.92),
  wyoming: padded(40.99, -111.06, 45.01, -104.05),
  'district of columbia': padded(38.79, -77.12, 39.0, -76.91),
  // Union of the 48 contiguous states' boxes (Florida Keys to Minnesota, Washington to Maine).
  'united states': padded(24.4, -124.84, 49.38, -66.95),
  'nova scotia': padded(43.37, -66.41, 47.04, -59.68),
  'new brunswick': padded(44.6, -69.06, 48.07, -63.77),
  ontario: padded(41.68, -95.16, 56.86, -74.34),
  quebec: padded(44.99, -79.76, 62.59, -57.1),
  'prince edward island': padded(45.85, -64.55, 47.6, -61.4),
  'newfoundland and labrador': padded(46.41, -67.82, 60.48, -52.32),
  manitoba: padded(49.0, -102.01, 60.0, -88.99),
  saskatchewan: padded(49.0, -110.01, 60.0, -101.36),
  alberta: padded(49.0, -120.0, 60.0, -110.0),
  'british columbia': padded(48.22, -139.06, 60.0, -114.05),
  yukon: padded(60.0, -141.0, 69.85, -123.81),
  'northwest territories': padded(60.0, -136.59, 79.13, -102.0),
  nunavut: padded(51.16, -120.68, 83.34, -59.61),
  canada: padded(41.68, -141.0, 83.34, -52.32),
  mexico: padded(14.38, -118.6, 32.72, -86.49),
  england: padded(49.86, -6.42, 55.81, 1.77),
  scotland: padded(54.63, -8.65, 60.86, -0.73),
  wales: padded(51.34, -5.35, 53.44, -2.65),
  ireland: padded(51.42, -10.48, 55.39, -5.43),
  germany: padded(47.27, 5.87, 55.06, 15.04),
  netherlands: NETHERLANDS,
  belgium: BELGIUM,
  'low countries': union(NETHERLANDS, BELGIUM),
  luxembourg: padded(49.45, 5.74, 50.18, 6.53),
  france: padded(41.33, -5.14, 51.09, 9.56),
  switzerland: padded(45.82, 5.96, 47.81, 10.49),
  austria: padded(46.37, 9.53, 49.02, 17.16),
  italy: padded(35.29, 6.63, 47.09, 18.78),
  spain: padded(27.43, -18.39, 43.99, 4.59),
  portugal: padded(29.83, -31.56, 42.15, -6.19),
  denmark: padded(54.45, 7.72, 57.95, 15.55),
  norway: padded(57.9, 4.5, 71.2, 31.2),
  sweden: padded(55.14, 10.59, 69.06, 24.18),
  finland: padded(59.45, 19.08, 70.09, 31.59),
  poland: padded(49.0, 14.07, 55.04, 24.15),
  czechia: padded(48.55, 12.09, 51.06, 18.86),
  hungary: padded(45.74, 16.11, 48.59, 22.9),
};

export interface Alias {
  /** Modern REGIONS key this historical name maps to. */
  region: string;
  /** When true, PlaceQueryBuilder rewrites the segment to `region` in the cleaned query. */
  rewrite: boolean;
  /** Colonial jurisdiction — region-checked against COLONIAL_NORTHEAST, not `region`. */
  historical?: boolean;
}

/** Historical names → modern region. Extend from `npm run geocode:audit` output, not guesses. */
export const ALIASES: Record<string, Alias> = {
  'plymouth colony': { region: 'massachusetts', rewrite: true, historical: true },
  'massachusetts bay colony': { region: 'massachusetts', rewrite: true, historical: true },
  'massachusetts bay': { region: 'massachusetts', rewrite: true, historical: true },
  'province of massachusetts bay': { region: 'massachusetts', rewrite: true, historical: true },
  'connecticut colony': { region: 'connecticut', rewrite: true, historical: true },
  'new haven colony': { region: 'connecticut', rewrite: true, historical: true },
  'colony of rhode island and providence plantations': {
    region: 'rhode island',
    rewrite: true,
    historical: true,
  },
  'rhode island and providence plantations': {
    region: 'rhode island',
    rewrite: true,
    historical: true,
  },
  'new york colony': { region: 'new york', rewrite: true, historical: true },
  'province of new york': { region: 'new york', rewrite: true, historical: true },
  'new netherland': { region: 'new york', rewrite: true, historical: true },
  'colony of nova scotia': { region: 'nova scotia', rewrite: true },
  'colony of virginia': { region: 'virginia', rewrite: true },
  'virginia colony': { region: 'virginia', rewrite: true },
  'province of pennsylvania': { region: 'pennsylvania', rewrite: true },
  'massachusetts bay province': { region: 'massachusetts', rewrite: true, historical: true },
  'massachusetts colony': { region: 'massachusetts', rewrite: true, historical: true },
  'new jersey colony': { region: 'new jersey', rewrite: true, historical: true },
  'province of new jersey': { region: 'new jersey', rewrite: true, historical: true },
  'east jersey': { region: 'new jersey', rewrite: true, historical: true },
  'west jersey': { region: 'new jersey', rewrite: true, historical: true },
  // Sub-regions: region lookup only — Nominatim understands these names in queries.
  'long island': { region: 'new york', rewrite: false },
  'l i': { region: 'new york', rewrite: true },
  "martha's vineyard": { region: 'massachusetts', rewrite: false },
  newfoundland: { region: 'newfoundland and labrador', rewrite: false },
  labrador: { region: 'newfoundland and labrador', rewrite: false },
  // Dutch country and province names (limburg omitted — also a Belgian province)
  nederland: { region: 'netherlands', rewrite: false },
  holland: { region: 'netherlands', rewrite: false },
  groningen: { region: 'netherlands', rewrite: false },
  friesland: { region: 'netherlands', rewrite: false },
  fryslan: { region: 'netherlands', rewrite: false },
  drenthe: { region: 'netherlands', rewrite: false },
  overijssel: { region: 'netherlands', rewrite: false },
  flevoland: { region: 'netherlands', rewrite: false },
  gelderland: { region: 'netherlands', rewrite: false },
  utrecht: { region: 'netherlands', rewrite: false },
  'noord-holland': { region: 'netherlands', rewrite: false },
  'zuid-holland': { region: 'netherlands', rewrite: false },
  zeeland: { region: 'netherlands', rewrite: false },
  'noord-brabant': { region: 'netherlands', rewrite: false },
  // The Habsburg (Spanish) Netherlands spanned today's Netherlands and Belgium
  'habsburgse nederlanden': { region: 'low countries', rewrite: false },
  'spanish netherlands': { region: 'low countries', rewrite: false },
  // The Dutch Republic (1581–1795)
  'republiek der zeven verenigde nederlanden': { region: 'netherlands', rewrite: true },
  // German states: region lookup only — Nominatim handles these names in queries.
  saxony: { region: 'germany', rewrite: false },
  prussia: { region: 'germany', rewrite: false },
  bavaria: { region: 'germany', rewrite: false },
  hesse: { region: 'germany', rewrite: false },
  wurttemberg: { region: 'germany', rewrite: false },
  palatinate: { region: 'germany', rewrite: false },
};

/**
 * US postal codes and traditional abbreviations, Canadian province codes, and
 * country abbreviations — each for a region that has a REGIONS box.
 */
export const ABBREVIATIONS: Record<string, string> = {
  ct: 'connecticut',
  conn: 'connecticut',
  'conn.': 'connecticut',
  ma: 'massachusetts',
  mass: 'massachusetts',
  'mass.': 'massachusetts',
  pa: 'pennsylvania',
  penn: 'pennsylvania',
  'penn.': 'pennsylvania',
  'penna.': 'pennsylvania',
  ny: 'new york',
  'n.y.': 'new york',
  nj: 'new jersey',
  'n.j.': 'new jersey',
  va: 'virginia',
  'va.': 'virginia',
  wv: 'west virginia',
  'w.va.': 'west virginia',
  md: 'maryland',
  'md.': 'maryland',
  de: 'delaware',
  'del.': 'delaware',
  nh: 'new hampshire',
  'n.h.': 'new hampshire',
  ri: 'rhode island',
  'r.i.': 'rhode island',
  vt: 'vermont',
  'vt.': 'vermont',
  me: 'maine',
  nc: 'north carolina',
  'n.c.': 'north carolina',
  sc: 'south carolina',
  's.c.': 'south carolina',
  ga: 'georgia',
  oh: 'ohio',
  in: 'indiana',
  il: 'illinois',
  ky: 'kentucky',
  tn: 'tennessee',
  mi: 'michigan',
  wi: 'wisconsin',
  ia: 'iowa',
  mo: 'missouri',
  al: 'alabama',
  'ala.': 'alabama',
  ak: 'alaska',
  az: 'arizona',
  'ariz.': 'arizona',
  ar: 'arkansas',
  'ark.': 'arkansas',
  ca: 'california',
  'cal.': 'california',
  'calif.': 'california',
  co: 'colorado',
  'colo.': 'colorado',
  fl: 'florida',
  'fla.': 'florida',
  hi: 'hawaii',
  id: 'idaho',
  ks: 'kansas',
  'kans.': 'kansas',
  la: 'louisiana',
  mn: 'minnesota',
  'minn.': 'minnesota',
  ms: 'mississippi',
  'miss.': 'mississippi',
  mt: 'montana',
  'mont.': 'montana',
  ne: 'nebraska',
  'neb.': 'nebraska',
  'nebr.': 'nebraska',
  nv: 'nevada',
  'nev.': 'nevada',
  nm: 'new mexico',
  'n.m.': 'new mexico',
  'n.mex.': 'new mexico',
  nd: 'north dakota',
  'n.d.': 'north dakota',
  'n.dak.': 'north dakota',
  ok: 'oklahoma',
  'okla.': 'oklahoma',
  or: 'oregon',
  'ore.': 'oregon',
  sd: 'south dakota',
  's.d.': 'south dakota',
  's.dak.': 'south dakota',
  tx: 'texas',
  'tex.': 'texas',
  ut: 'utah',
  wa: 'washington',
  'wash.': 'washington',
  wy: 'wyoming',
  'wyo.': 'wyoming',
  dc: 'district of columbia',
  'd.c.': 'district of columbia',
  usa: 'united states',
  'u.s.a.': 'united states',
  'u.s.': 'united states',
  'united states of america': 'united states',
  ns: 'nova scotia',
  nb: 'new brunswick',
  qc: 'quebec',
  pq: 'quebec',
  'que.': 'quebec',
  on: 'ontario',
  'ont.': 'ontario',
  pe: 'prince edward island',
  pei: 'prince edward island',
  'p.e.i.': 'prince edward island',
  nl: 'newfoundland and labrador',
  nf: 'newfoundland and labrador',
  'nfld.': 'newfoundland and labrador',
  mb: 'manitoba',
  'man.': 'manitoba',
  sk: 'saskatchewan',
  'sask.': 'saskatchewan',
  ab: 'alberta',
  'alta.': 'alberta',
  bc: 'british columbia',
  'b.c.': 'british columbia',
  yt: 'yukon',
  nt: 'northwest territories',
  nu: 'nunavut',
  'can.': 'canada',
};

/**
 * Umbrella names for colonial North America. FamilySearch-style terms that WikiTree's
 * North American Place Names page advises against, but exports contain them. Never a
 * useful query component: dropped as segments and stripped as trailing words.
 */
export const COLONIAL_UMBRELLAS = [
  'british colonial america',
  'colonial america',
  'british america',
];

/** Country words written at the end of a comma-less segment ("… nova scotia canada"). */
const TRAILING_COUNTRY_WORDS = ['united states of america', 'united states', 'usa', 'canada'];

/**
 * Codes that are also common words (or "co" = county); never matched as a trailing
 * word. They still match as a whole comma segment ("portland, or").
 */
const NO_SUFFIX_MATCH = new Set([
  'in',
  'me',
  'de',
  'or',
  'ok',
  'hi',
  'la',
  'id',
  'co',
  'al',
  'on',
  'ne',
]);

/** Longest region name, in words — bounds the trailing-word search. */
const MAX_REGION_WORDS = 5;

/** A token and, if it ends in ".", the token without it ("ma." → "ma"). */
function spellings(token: string): string[] {
  return token.endsWith('.') ? [token, token.slice(0, -1)] : [token];
}

/** Resolve a whole token (segment or trailing words) to a REGIONS key, or null. */
function lookupToken(token: string): string | null {
  for (const t of spellings(token)) {
    if (t in REGIONS) {
      return t;
    }
    const alias = ALIASES[t];
    if (alias !== undefined) {
      return alias.region;
    }
    const abbreviation = ABBREVIATIONS[t];
    if (abbreviation !== undefined) {
      return abbreviation;
    }
  }
  return null;
}

/** The region a token is checked against: COLONIAL_NORTHEAST for colonial jurisdictions. */
function checkRegionFor(token: string): string | null {
  for (const t of spellings(token)) {
    if (ALIASES[t]?.historical === true) {
      return COLONIAL_NORTHEAST;
    }
  }
  return lookupToken(token);
}

function stripSuffix(text: string, suffixes: string[]): string {
  for (const s of suffixes) {
    if (text.endsWith(` ${s}`)) {
      return text.slice(0, -(s.length + 1)).trim();
    }
  }
  return text;
}

/**
 * Comma segments of a normalized key, tidied for matching: ";" also separates,
 * parenthetical notes are removed ("plymouth colony (massachusetts bay in 1691)"),
 * colonial umbrella segments are dropped, and a trailing umbrella or country word on
 * a multi-word segment is stripped. Empty segments are removed.
 */
export function segmentsOf(normalized: string): string[] {
  const out: string[] = [];
  for (const raw of normalized.split(/[,;]/)) {
    let s = raw
      .replace(/\([^)]*\)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (COLONIAL_UMBRELLAS.includes(s)) {
      continue;
    }
    s = stripSuffix(stripSuffix(s, COLONIAL_UMBRELLAS), TRAILING_COUNTRY_WORDS);
    if (s.length > 0) {
      out.push(s);
    }
  }
  return out;
}

/**
 * The region a normalized place string names, or null.
 *
 * Pass 1: the last comma segment that is exactly a region, alias, or abbreviation.
 * Segment-level only, so "new england" ≠ "england", "west virginia" ≠ "virginia",
 * and "york, england" never matches "new york".
 *
 * Pass 2 (only if pass 1 finds no state/province): trailing words of a segment,
 * longest first, for comma-less keys such as "new london ct".
 *
 * A country-level segment ("united states", "canada") is used only when neither
 * pass finds a state or province. Colonial jurisdictions return COLONIAL_NORTHEAST.
 */
export function expectedRegion(normalized: string): string | null {
  const segments = segmentsOf(normalized);
  let countryHit: string | null = null;

  for (let i = segments.length - 1; i >= 0; i--) {
    const hit = checkRegionFor(segments[i]!);
    if (hit === null) {
      continue;
    }
    if (!COUNTRY_LEVEL.has(hit)) {
      return hit;
    }
    countryHit ??= hit;
  }

  for (let i = segments.length - 1; i >= 0; i--) {
    const split = trailingRegion(segments[i]!);
    if (split !== null && !COUNTRY_LEVEL.has(split.check)) {
      return split.check;
    }
  }

  return countryHit;
}

/**
 * Split a trailing region name off one segment: "new london ct" →
 * { locality: "new london", region: "connecticut", check: "connecticut" }. `region`
 * is the modern name used in queries; `check` is the region to judge results against.
 * Longest suffix first, so "wheeling west virginia" yields west virginia, not
 * virginia. Needs at least one locality word; common-word codes (in/me/de) never
 * match. Null when no suffix is a region, alias, or abbreviation.
 */
export function trailingRegion(
  segment: string,
): { locality: string; region: string; check: string } | null {
  const words = segment.trim().split(' ');
  if (words.length < 2) {
    return null;
  }
  const maxLen = Math.min(MAX_REGION_WORDS, words.length - 1);
  for (let len = maxLen; len >= 1; len--) {
    const suffix = words.slice(-len).join(' ');
    if (len === 1 && NO_SUFFIX_MATCH.has(suffix)) {
      continue;
    }
    const region = lookupToken(suffix);
    if (region !== null) {
      return {
        locality: words.slice(0, -len).join(' '),
        region,
        check: checkRegionFor(suffix) ?? region,
      };
    }
  }
  return null;
}

/** True when `query` is a single segment that is only a region name (no locality). */
export function isBareRegion(query: string): boolean {
  return !query.includes(',') && lookupToken(query.trim()) !== null;
}

/** Whether `box` contains the point. */
export function inBox(box: Box, lat: number, lng: number): boolean {
  return lat >= box.south && lat <= box.north && lng >= box.west && lng <= box.east;
}

/**
 * true — point is inside the region the place names; false — outside;
 * null — the place names no known region, so no judgement.
 */
export function withinRegion(normalized: string, lat: number, lng: number): boolean | null {
  const region = expectedRegion(normalized);
  if (region === null) {
    return null;
  }
  const box = REGIONS[region];
  return box === undefined ? null : inBox(box, lat, lng);
}
